import React, { useEffect, useState } from 'react'
import { getReceiverState, subscribeToState } from '../../../core/state/ReceiverStore'
import { toggleIngredientsView, updateServings, createRecipeNavigation } from '../../../core/state/RecipeState'
import { RecipeStep, RecipeIngredients, RecipeNavigation } from '../../recipe/components'
import TouchControls from './TouchControls'
import LoadingScreen from './LoadingScreen'
import { Recipe } from '../../recipe/types'

interface LayoutProps {
  hasTouchScreen: boolean
}

const Layout: React.FC<LayoutProps> = ({ hasTouchScreen }) => {
  console.log('⚡ Layout: Initializing with hasTouchScreen =', hasTouchScreen)
  const [recipe, setRecipe] = useState<Recipe | null>(null)
  const [currentStepIndex, setCurrentStepIndex] = useState(0)
  const [showingIngredients, setShowingIngredients] = useState(false)
  const [servings, setServings] = useState(4)
  const [showDebug, setShowDebug] = useState(false)
  
  // Get navigation controls
  console.log(`📱 Layout initializing with touch support: ${hasTouchScreen ? 'enabled' : 'disabled'}`);
  const { goToNextStep, goToPreviousStep } = createRecipeNavigation()
  
  // Debug helper to log touch events
  useEffect(() => {
    // Log when touch events occur on the document to help debug touch issues
    const logTouch = (e: TouchEvent) => {
      console.log(`📱 Touch ${e.type} event detected in Layout:`, {
        type: e.type,
        touches: e.touches.length,
        target: (e.target as any)?.tagName || 'unknown',
        targetId: (e.target as any)?.id || 'no-id',
        targetClass: (e.target as any)?.className || 'no-class',
        bubbles: e.bubbles,
        clientX: e.touches[0]?.clientX,
        clientY: e.touches[0]?.clientY
      });

      // Important: Add this to verify if events are being stopped
      // or if they're just not reaching the right elements
      console.log('Touch event path:', e.composedPath().map(el => (el as any)?.tagName || 'unknown').join(' > '));
    };
    
    // Add event listeners for all touch events
    document.addEventListener('touchstart', logTouch);
    document.addEventListener('touchend', logTouch);
    document.addEventListener('touchmove', logTouch);
    document.addEventListener('touchcancel', logTouch);
    
    // Cleanup
    return () => {
      document.removeEventListener('touchstart', logTouch);
      document.removeEventListener('touchend', logTouch);
      document.removeEventListener('touchmove', logTouch);
      document.removeEventListener('touchcancel', logTouch);
    };
  }, []);
  
  // Subscribe to state changes from the store
  useEffect(() => {
    // Log current state at component mount
    const initialState = getReceiverState();
    console.log('Initial state on Layout mount:', initialState);
    console.log('Recipe available in state:', !!initialState.recipe);
    
    if (initialState.recipe && !recipe) {
      console.log('Setting recipe from initial state');
      setRecipe(initialState.recipe);
      setCurrentStepIndex(initialState.currentStepIndex);
      setShowingIngredients(initialState.showingIngredients);
      setServings(initialState.servings);
    }
    
    // Subscribe to state changes
    const unsubscribe = subscribeToState((state) => {
      console.log('State change detected in Layout:');
      console.log('- Recipe present:', !!state.recipe);
      console.log('- Current step index:', state.currentStepIndex);
      console.log('- Showing ingredients:', state.showingIngredients);
      
      // Set recipe
      if (state.recipe) {
        console.log('Updating recipe in Layout from new state');
        setRecipe(state.recipe);
      }
      
      // Update current step
      if (state.currentStepIndex !== currentStepIndex) {
        setCurrentStepIndex(state.currentStepIndex);
      }
      
      // Update ingredients view state
      if (state.showingIngredients !== showingIngredients) {
        setShowingIngredients(state.showingIngredients);
      }
      
      // Update servings
      if (state.servings !== servings) {
        setServings(state.servings);
      }
    });
    
    return () => {
      unsubscribe()
    }
  }, [currentStepIndex, showingIngredients, servings])
  
  // Handler for toggling ingredients view
  const handleToggleIngredients = () => {
    console.log('🔍 Layout: Toggle ingredients handler called');
    toggleIngredientsView(!showingIngredients)
  }
  
  // Handler for updating servings
  const handleUpdateServings = (newServings: number) => {
    console.log(`🍲 Layout: Update servings handler called with value: ${newServings}`);
    updateServings(newServings)
  }
  
  // Force reload recipe from state (development helper)
  const forceReloadRecipe = () => {
    const state = getReceiverState()
    console.log('Current receiver state:', state)
    if (state.recipe) {
      setRecipe(state.recipe)
      setCurrentStepIndex(state.currentStepIndex)
      setShowingIngredients(state.showingIngredients)
      setServings(state.servings)
    } else {
      console.warn('No recipe in state to reload')
    }
  }
  
  // If no recipe is loaded, show a loading/waiting screen with a test button
  if (!recipe) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gray-900 text-white p-8">
        <LoadingScreen />
        
        {/* Test Button (only in development) */}
        {process.env.NODE_ENV === 'development' && (
          <div className="mt-8 border-t border-gray-800 pt-8 w-full max-w-md">
            <h2 className="text-2xl font-bold text-center mb-4">Development Controls</h2>
            <button
              onClick={() => {
                console.log('LOAD TEST RECIPE button clicked from Layout');
                try {
                  // Import the MessageHandler directly
                  import('../../../core/cast/MessageHandler').then(({ handleSenderMessage }) => {
                    // Create fake event to simulate a sender message
                    const event = {
                      senderId: 'dev-layout',
                      data: {
                        command: 'LOAD_TEST_RECIPE',
                        timestamp: Date.now()
                      }
                    };
                    handleSenderMessage(event);
                    console.log('Manually triggered LOAD_TEST_RECIPE handling from Layout');
                  });
                } catch (error) {
                  console.error('Failed to trigger test recipe loading from Layout:', error);
                }
              }}
              className="w-full bg-red-600 hover:bg-red-700 text-white px-6 py-4 rounded-xl text-xl font-bold"
            >
              LOAD TEST RECIPE
            </button>
            <p className="text-gray-500 text-sm text-center mt-2">
              This button loads a test recipe directly on the receiver
            </p>
          </div>
        )}
      </div>
    );
  }
  
  // Calculate navigation state
  const totalSteps = recipe.steps?.length || 0;
  const canGoNext = currentStepIndex < totalSteps - 1;
  const canGoPrevious = currentStepIndex > 0;
  
  // Safety check - if recipe has structure issues, show a simplified version
  if (!Array.isArray(recipe.steps) || !recipe.steps.length) {
    console.error('Recipe has no valid steps array, showing fallback display');
    return (
      <div className="min-h-screen flex flex-col bg-gray-900 text-white p-10">
        <h1 className="text-3xl font-bold mb-8">RECIPE ERROR</h1>
        <h2 className="text-2xl mb-6">{recipe.title || 'Untitled Recipe'}</h2>
        
        <div className="bg-red-800 p-4 mb-6">
          <p>Recipe format error. Debug info:</p>
          <pre className="text-xs mt-2 overflow-auto">
            {JSON.stringify({
              recipeId: recipe.id,
              hasTitle: !!recipe.title,
              stepsIsArray: Array.isArray(recipe.steps),
              stepsLength: Array.isArray(recipe.steps) ? recipe.steps.length : 'N/A',
              ingredientsIsArray: Array.isArray(recipe.ingredients),
              ingredientsLength: Array.isArray(recipe.ingredients) ? recipe.ingredients.length : 'N/A'
            }, null, 2)}
          </pre>
        </div>
        
        <button 
          className="bg-blue-700 text-white px-4 py-2 rounded mt-4"
          onClick={forceReloadRecipe}
        >
          Try Reload Recipe
        </button>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen flex flex-col bg-gray-900 text-white">
      {/* Debug toggle - a small touchable area in the top-right corner */}
      <div
        className="fixed top-0 right-0 w-16 h-16 z-50"
        onClick={() => setShowDebug(prev => !prev)}
      >
        {/* Small debug icon/indicator */}
        <div className="absolute top-2 right-2 w-3 h-3 rounded-full bg-green-500 opacity-50"></div>
      </div>
      
      {/* Debug overlay */}
      {showDebug && (
        <div className="fixed top-0 right-0 p-4 bg-black bg-opacity-80 text-green-400 font-mono text-xs max-w-md max-h-screen overflow-auto z-40">
          <h3 className="font-bold mb-2">Current State:</h3>
          <pre>{JSON.stringify({
            recipe: {
              id: recipe.id,
              title: recipe.title,
              steps: recipe.steps.length,
              ingredients: recipe.ingredients.length
            },
            currentStepIndex,
            totalSteps,
            showingIngredients,
            servings
          }, null, 2)}</pre>
          <button 
            className="mt-4 bg-green-800 hover:bg-green-700 text-white px-3 py-1 rounded"
            onClick={forceReloadRecipe}
          >
            Reload Recipe
          </button>
        </div>
      )}
      
      {/* Header with Recipe Title */}
      <header className="bg-gray-800 shadow-lg py-4 px-8">
        <h1 className="text-3xl font-bold truncate">
          {recipe.title}
        </h1>
      </header>
      
      {/* Main Content Area */}
      <main className="flex-grow flex justify-center items-center overflow-hidden">
        {showingIngredients ? (
          <RecipeIngredients 
            ingredients={recipe.ingredients} 
            servings={servings}
            originalServings={recipe.servings}
            hasTouchScreen={hasTouchScreen}
          />
        ) : (
          <>
            {/* Standard recipe step component */}
            <RecipeStep 
              step={recipe.steps[currentStepIndex]} 
              stepNumber={currentStepIndex + 1}
              totalSteps={totalSteps}
              hasTouchScreen={hasTouchScreen}
            />
            
            {/* Fallback display for recipe step content */}
            <div className="absolute bottom-32 right-8 p-4 bg-black bg-opacity-70 rounded max-w-xs">
              <h4 className="text-sm font-bold text-gray-400">Fallback: Step {currentStepIndex + 1}/{totalSteps}</h4>
              <p className="text-xs mt-1 text-white">
                {recipe.steps[currentStepIndex]?.instructions || 'No instructions'}
              </p>
            </div>
          </>
        )}
      </main>
      
      {/* Footer/Navigation Area */}
      {hasTouchScreen ? (
        <TouchControls 
          onPrevious={goToPreviousStep}
          onNext={goToNextStep}
          onToggleIngredients={handleToggleIngredients}
          onUpdateServings={handleUpdateServings}
          currentServings={servings}
          showingIngredients={showingIngredients}
          canGoNext={canGoNext}
          canGoPrevious={canGoPrevious}
        />
      ) : (
        <RecipeNavigation 
          currentStep={currentStepIndex}
          totalSteps={totalSteps}
          onPrevious={goToPreviousStep}
          onNext={goToNextStep}
          onShowIngredients={handleToggleIngredients}
          hasTouchScreen={hasTouchScreen}
        />
      )}
    </div>
  )
}

export default Layout
